export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('Không đọc được file.'))
    reader.readAsDataURL(file)
  })
}

export function splitFileName(name: string): { base: string; ext: string } {
  const index = name.lastIndexOf('.')
  if (index <= 0) return { base: name, ext: '' }
  return { base: name.slice(0, index), ext: name.slice(index) }
}

export function uniqueName(existing: string[], desired: string): string {
  const taken = new Set(existing.map((n) => n.toLowerCase()))
  if (!taken.has(desired.toLowerCase())) return desired
  const { base, ext } = splitFileName(desired)
  let i = 2
  let next = `${base} (${i})${ext}`
  while (taken.has(next.toLowerCase())) {
    i += 1
    next = `${base} (${i})${ext}`
  }
  return next
}

export function formatFileSize(bytes?: number): string {
  if (bytes == null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

export function fileTypeLabel(name: string, kind: 'file' | 'folder'): string {
  if (kind === 'folder') return 'Thư mục'
  const ext = name.split('.').pop()?.toLowerCase() ?? ''
  const map: Record<string, string> = {
    pdf: 'PDF',
    dwg: 'DWG',
    dxf: 'DXF',
    xlsx: 'Excel',
    xlsm: 'Excel',
    xls: 'Excel',
    csv: 'CSV',
    jpg: 'Ảnh JPEG',
    jpeg: 'Ảnh JPEG',
    png: 'Ảnh PNG',
    webp: 'Ảnh WEBP',
    gif: 'Ảnh GIF',
    zip: 'ZIP',
    rar: 'RAR',
    docx: 'Word',
    doc: 'Word',
  }
  return map[ext] ?? (ext ? ext.toUpperCase() : 'File')
}

export function isImageName(name: string): boolean {
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)
}

export function isPdfName(name: string): boolean {
  return /\.pdf$/i.test(name)
}

export function isBomName(name: string): boolean {
  return /\.(xlsx|xlsm|xls)$/i.test(name)
}

export async function downloadDataUrl(name: string, dataUrl: string): Promise<void> {
  const response = await fetch(dataUrl)
  if (!response.ok) throw new Error('Không tải được file.')
  const buffer = await response.arrayBuffer()
  const blob = new Blob([buffer], { type: 'application/octet-stream' })
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = name
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(objectUrl)
}

export async function dataUrlToArrayBuffer(dataUrl: string): Promise<ArrayBuffer> {
  const response = await fetch(dataUrl)
  if (!response.ok) throw new Error('Không đọc được dữ liệu file.')
  return response.arrayBuffer()
}

/** Valid minimal multi-page PDF that pdf.js / react-pdf can render. */
export function makePdfDataUrl(title: string): string {
  const safe = title.replace(/[()\\]/g, ' ').slice(0, 80)
  const page = (label: string, y: number) => {
    const content = `BT /F1 24 Tf 72 ${y} Td (${label}) Tj ET`
    return content
  }
  const c1 = page(`DRAWING - ${safe}`, 720)
  const c2 = page(`Page 2 - ${safe}`, 720)
  const objects = [
    '1 0 obj<< /Type /Catalog /Pages 2 0 R >>endobj',
    '2 0 obj<< /Type /Pages /Kids [3 0 R 5 0 R] /Count 2 >>endobj',
    '3 0 obj<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 7 0 R >> >> >>endobj',
    `4 0 obj<< /Length ${c1.length} >>stream\n${c1}\nendstream\nendobj`,
    '5 0 obj<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 6 0 R /Resources << /Font << /F1 7 0 R >> >> >>endobj',
    `6 0 obj<< /Length ${c2.length} >>stream\n${c2}\nendstream\nendobj`,
    '7 0 obj<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>endobj',
  ]
  let body = '%PDF-1.4\n'
  const offsets: number[] = [0]
  for (const obj of objects) {
    offsets.push(body.length)
    body += `${obj}\n`
  }
  const xrefStart = body.length
  let xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`
  for (let i = 1; i <= objects.length; i += 1) {
    xref += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`
  }
  const trailer = `trailer<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`
  const pdf = body + xref + trailer
  return `data:application/pdf;base64,${btoa(pdf)}`
}
